// CANCEL-002 — bottom-sheet request form.
// CANCEL-003 — live email preview via the "See the email" disclosure.
//
// Lets the engineer pick a reason and (optionally) add a note, then submits
// the request. The parent owns the mutation; this component is driven by
// its `isSending` / `isFailed` props and surfaces inline error/retry UI.
//
// Ported from octopus-mock/components/orders/cancel-request.tsx
// (CancelRequestSheet).
import { useId, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { AlertCircle, ChevronDown, Loader2, Mail, Phone, X } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '../../radix'
import type { CancellationReason } from '../../../auth/services/auth-api'
import { buildCancelEmail } from '../../../utils/cancelEmail'
import { CANCEL_NOTE_MAX_LENGTH, CANCEL_REASONS } from './constants'

interface CancelRequestSheetProps {
  open: boolean
  onClose: () => void
  onSubmit: (reason: CancellationReason, note: string) => void
  isSending: boolean
  isFailed: boolean
  // Branch + supplier identity + contact details.
  supplierName: string
  branchName: string
  branchEmail: string
  branchPhone: string
  // Fields needed to render the live email preview (CANCEL-003 AC2).
  orderNumber: string
  supplierOrderNumber: string | null
  accountNumber: string
  jobNumber: string
  itemCount: number
  totalAmount: number
  fulfillmentMode: 'pickup' | 'delivery'
  deliveryAddress: string | null
  engineerName: string
  engineerEmail: string
}

export function CancelRequestSheet({
  open,
  onClose,
  onSubmit,
  isSending,
  isFailed,
  supplierName,
  branchName,
  branchEmail,
  branchPhone,
  orderNumber,
  supplierOrderNumber,
  accountNumber,
  jobNumber,
  itemCount,
  totalAmount,
  fulfillmentMode,
  deliveryAddress,
  engineerName,
  engineerEmail,
}: CancelRequestSheetProps) {
  const [reason, setReason] = useState<CancellationReason | null>(null)
  const [note, setNote] = useState('')
  const [reasonError, setReasonError] = useState(false)
  const [showEmail, setShowEmail] = useState(false)
  const firstReasonRef = useRef<HTMLInputElement>(null)
  const submitRef = useRef<HTMLButtonElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const id = useId()
  const legendId = `${id}-legend`
  const reasonErrorId = `${id}-reason-error`
  const noteId = `${id}-note`
  const counterId = `${id}-counter`
  const previewId = `${id}-preview`

  // Reason label pulled from CANCEL_REASONS so the preview matches the
  // backend's enum .label resolution exactly.
  const reasonLabel = reason
    ? (CANCEL_REASONS.find((option) => option.value === reason)?.label ?? null)
    : null

  const emailPreview = buildCancelEmail({
    orderNumber,
    supplierName,
    branchName,
    supplierOrderNumber,
    accountNumber,
    jobNumber,
    itemCount,
    totalAmount,
    fulfillmentMode,
    deliveryAddress,
    engineerName,
    engineerEmail,
    branchEmail,
    reason,
    reasonLabel,
    note,
  })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (isSending) return
    if (!reason) {
      setReasonError(true)
      firstReasonRef.current?.focus()
      return
    }
    // The inputs are about to be disabled; keep focus on the button.
    submitRef.current?.focus()
    onSubmit(reason, note.trim())
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Lock the sheet while sending (AC8).
        if (next || isSending) return
        // Reset error on close; keep the reason + note so Try again from
        // the entry works too (the spec preserves inputs on retry anyway).
        setReasonError(false)
        onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          onPointerDownOutside={(event) => {
            if (isSending) event.preventDefault()
          }}
          onEscapeKeyDown={(event) => {
            if (isSending) event.preventDefault()
          }}
          className="fixed left-1/2 bottom-0 sm:top-1/2 sm:bottom-auto -translate-x-1/2 sm:-translate-y-1/2 z-50 w-full sm:w-[90vw] sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-t-2xl sm:rounded-2xl shadow-xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
            <Dialog.Title className="text-base font-bold text-gray-900">
              Request cancellation
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                disabled={isSending}
                className="min-h-[44px] min-w-[44px] -mr-2 flex items-center justify-center text-gray-500 disabled:opacity-40"
              >
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={handleSubmit}
            noValidate
            className="flex-1 overflow-y-auto px-4 py-4 space-y-5"
          >
            <p className="text-sm text-gray-700 break-words">
              We&apos;ll email {supplierName} {branchName} at{' '}
              <span className="font-semibold text-gray-900 break-all">
                {branchEmail}
              </span>{' '}
              and copy you in. The order isn&apos;t cancelled until the branch
              confirms.
            </p>

            <fieldset
              aria-describedby={reasonError ? reasonErrorId : undefined}
              disabled={isSending}
            >
              <legend
                id={legendId}
                className="mb-2 text-sm font-medium text-gray-900"
              >
                Why do you want to cancel?
              </legend>
              {reasonError && (
                <p
                  id={reasonErrorId}
                  className="mb-2 flex items-start gap-1.5 text-sm font-medium text-red-600"
                >
                  <AlertCircle className="mt-0.5 w-4 h-4 flex-shrink-0" />
                  <span>Choose a reason</span>
                </p>
              )}
              <div
                className={clsx(
                  'divide-y divide-gray-100 overflow-hidden rounded-xl border',
                  reasonError ? 'border-red-500' : 'border-gray-200'
                )}
              >
                {CANCEL_REASONS.map((option, i) => (
                  <label
                    key={option.value}
                    className="flex min-h-[48px] cursor-pointer items-center gap-3 px-3 py-2 text-sm text-gray-900 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60 has-[:focus-visible]:bg-gray-50"
                  >
                    <input
                      ref={i === 0 ? firstReasonRef : undefined}
                      type="radio"
                      name={`${id}-reason`}
                      value={option.value}
                      checked={reason === option.value}
                      onChange={() => {
                        setReason(option.value)
                        // Clear the "choose a reason" error inline on pick,
                        // not via an effect — AC3.
                        setReasonError(false)
                      }}
                      aria-invalid={reasonError ? true : undefined}
                      aria-describedby={
                        reasonError ? reasonErrorId : undefined
                      }
                      className="h-5 w-5 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <label
                htmlFor={noteId}
                className="mb-2 block text-sm font-medium text-gray-900"
              >
                Note for the branch{' '}
                <span className="font-normal text-gray-600">(optional)</span>
              </label>
              <textarea
                id={noteId}
                value={note}
                onChange={(e) =>
                  setNote(e.target.value.slice(0, CANCEL_NOTE_MAX_LENGTH))
                }
                maxLength={CANCEL_NOTE_MAX_LENGTH}
                rows={3}
                disabled={isSending}
                aria-describedby={counterId}
                className="block w-full resize-y rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 placeholder:text-gray-500 focus-visible:border-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:opacity-60"
              />
              <p
                id={counterId}
                className="mt-1.5 text-right text-xs tabular-nums text-gray-600"
              >
                {note.length}/{CANCEL_NOTE_MAX_LENGTH}
              </p>
            </div>

            {/* CANCEL-003: live email preview. Updates as the engineer picks
                a reason and types a note. Closed by default — below the fold
                on a phone, so scrolls into view when opened. */}
            <div className="rounded-xl border border-gray-200">
              <button
                type="button"
                aria-expanded={showEmail}
                aria-controls={previewId}
                disabled={isSending}
                onClick={() => {
                  const next = !showEmail
                  setShowEmail(next)
                  if (next) {
                    requestAnimationFrame(() =>
                      previewRef.current?.scrollIntoView({ block: 'nearest' })
                    )
                  }
                }}
                className="flex min-h-[48px] w-full items-center gap-2 rounded-xl px-3 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:opacity-60"
              >
                <Mail className="w-4 h-4 flex-shrink-0 text-gray-500" />
                <span className="flex-1">See the email</span>
                <ChevronDown
                  className={clsx(
                    'w-4 h-4 flex-shrink-0 text-gray-500 motion-safe:transition-transform',
                    showEmail && 'rotate-180'
                  )}
                />
              </button>
              {showEmail && (
                <div
                  id={previewId}
                  ref={previewRef}
                  className="border-t border-gray-200 px-3 py-3 text-sm"
                >
                  <dl className="space-y-1 text-gray-700">
                    <div className="flex gap-2">
                      <dt className="w-16 flex-shrink-0 text-gray-500">To</dt>
                      <dd className="min-w-0 font-medium text-gray-900 break-all">
                        {emailPreview.to}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="w-16 flex-shrink-0 text-gray-500">CC</dt>
                      <dd className="min-w-0 font-medium text-gray-900 break-all">
                        {emailPreview.cc}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="w-16 flex-shrink-0 text-gray-500">Subject</dt>
                      <dd className="min-w-0 font-medium text-gray-900 break-words">
                        {emailPreview.subject}
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-3 whitespace-pre-line rounded-lg bg-gray-50 px-3 py-2.5 text-gray-900 break-words">
                    {emailPreview.body}
                  </p>
                </div>
              )}
            </div>
          </form>

          <div className="border-t border-gray-200 bg-white px-4 py-3 space-y-2.5">
            {isFailed && (
              <div
                role="alert"
                className="space-y-2.5 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-gray-900"
              >
                <p className="flex items-start gap-2 font-medium">
                  <AlertCircle className="mt-0.5 w-4 h-4 flex-shrink-0 text-red-600" />
                  We couldn&apos;t send the email. Try again, or call the
                  branch if it&apos;s urgent.
                </p>
                {branchPhone && (
                  <Button asChild variant="outline" className="min-h-[44px] w-full">
                    <a href={`tel:${branchPhone}`}>
                      <Phone className="w-4 h-4" />
                      Call branch
                      <span className="font-normal tabular-nums">
                        {branchPhone}
                      </span>
                    </a>
                  </Button>
                )}
              </div>
            )}
            <Button
              ref={submitRef}
              type="submit"
              onClick={handleSubmit}
              className="min-h-[44px] w-full"
              aria-disabled={isSending || undefined}
            >
              {isSending ? (
                <>
                  <Loader2 className="w-4 h-4 motion-safe:animate-spin" />
                  Sending…
                </>
              ) : isFailed ? (
                'Try again'
              ) : (
                'Send request'
              )}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
